// The LP pipeline update: the branded two-page document "Export pipeline"
// prints. Pure - takes the rows services/pipelineExport.js loads and returns
// a standalone HTML document.
//
//   page 1  Pipeline Activity - rolling 30 days: deals that entered the
//           pipeline, deals passed, LOIs submitted, where the new deals stand
//   page 2  Active Pipeline - every Underwriting / LOI / Diligence deal with
//           price, sourcing, broker, CFO date and date added
//
// LPs receive this, so it carries price (deals.underwritten_price) and never a
// return metric. Brand: Montserrat, PP red / charcoal / grays, the brand book's
// split rules and "_" separators.

import { parseDealName } from "./parseDealName";

const STATES = { FL: "FL", FLORIDA: "FL", TX: "TX", TEXAS: "TX", GA: "GA", GEORGIA: "GA" };
const STREET = /\b(rd|road|st|street|ave|avenue|blvd|boulevard|dr|drive|cir|circle|way|hwy|highway|pkwy|parkway|ln|lane|ct|court|pl|place|trl|trail|loop|east|west|north|south|e|w|n|s)\.?$/i;

/** "Largo, FL" from a deal's own first address, else Google's matched address. */
export function cityState(addresses, geocodeMatched) {
  const fromParts = (raw) => {
    const parts = String(raw || "").replace(/,?\s*USA$/i, "").split(",").map((p) => p.trim()).filter(Boolean);
    if (parts.length < 2) return null;
    const st = STATES[parts[parts.length - 1].replace(/[\d\s-]+$/, "").trim().toUpperCase()];
    if (!st) return null;
    const prev = parts[parts.length - 2];
    if (!/\d/.test(prev)) return `${prev}, ${st}`;
    // "3208 E Belt Line Rd Farmers Branch" - the city is what follows the last street word
    const words = prev.split(/\s+/);
    let i = words.length - 1;
    while (i > 0 && !STREET.test(words[i - 1]) && !/\d/.test(words[i - 1])) i -= 1;
    const city = words.slice(i).join(" ");
    return city && !/\d/.test(city) && i > 0 ? `${city.replace(/\b\w/g, (c) => c.toUpperCase())}, ${st}` : null;
  };
  return fromParts((addresses || [])[0]) || fromParts(geocodeMatched) || "";
}

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const money = (v) => (v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : `$${Math.round(v).toLocaleString("en-US")}`);
const mdy = (iso) => { const [y, m, d] = iso.slice(0, 10).split("-"); return `${+m}/${+d}/${y.slice(2)}`; };
const monDay = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
const isoWeek = (d) => {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  t.setUTCDate(t.getUTCDate() + 4 - (t.getUTCDay() || 7));
  return Math.ceil(((t - Date.UTC(t.getUTCFullYear(), 0, 1)) / 86400000 + 1) / 7);
};

const OUTCOMES = [
  ["Active", ["Underwriting", "LOI Submitted", "In Diligence", "Acquired"], "var(--red)"],
  ["Tracking", ["Tracking", "Screening", "Sourcing"], "var(--ink)"],
  ["Passed", ["Passed"], "#a9a6a5"],
  ["Lost to another bid", ["Lost"], "var(--gray)"],
  ["Did not proceed", ["Dead"], "#ebe9e8"],
];
const REASON_LABEL = { "Off-market / off-strategy": "Off-strategy (market / type)" };

/** Shape the raw Supabase rows into what the two pages show. */
export function buildReport({ today, since, active, entered, killed, loiDealIds }) {
  const deals = active.map((r) => ({
    name: parseDealName(r.source_notes) || r.deal_name || "Untitled deal",
    stage: r.deal_stage,
    city: cityState(r.addresses, r.geocode_matched),
    market: (r.market || "").split(",")[0].trim(),
    sf: Number(r.square_footage) || 0,
    price: Number(r.underwritten_price) || 0,
    on: !!r.marketed,
    broker: r.broker_firm || "",
    cfo: r.call_for_offers_date || null,
    added: (r.created_at || "").slice(0, 10),
  }));
  const passed = killed.filter((k) => k.deal_stage === "Passed");
  const count = (rows, key) => Object.entries(rows.reduce((m, r) => ({ ...m, [key(r)]: (m[key(r)] || 0) + 1 }), {}))
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  return {
    today, since, deals,
    entered: entered.length,
    enteredSf: entered.reduce((s, r) => s + (Number(r.square_footage) || 0), 0),
    outcomes: OUTCOMES.map(([label, stages, color]) => ({ label, color, n: entered.filter((r) => stages.includes(r.deal_stage)).length })),
    byMarket: count(entered, (r) => (r.market || "Other").split(",")[0].trim()),
    passed: passed.length,
    reasons: count(passed, (k) => REASON_LABEL[k.killed_reason] || k.killed_reason || "Other"),
    lost: killed.filter((k) => k.deal_stage === "Lost").length,
    dead: killed.filter((k) => k.deal_stage === "Dead").length,
    lois: new Set(loiDealIds).size,
  };
}

const bars = (items) => {
  const top = Math.max(1, ...items.map(([, v]) => v));
  return items.map(([k, v]) => `
        <div class="bar"><span class="bl">${esc(k)}</span><span class="bt"><span style="width:${(v / top * 100).toFixed(1)}%"></span></span><span class="bv">${v}</span></div>`).join("");
};

function stageTable(deals, stage) {
  const ds = deals.filter((d) => d.stage === stage).sort((a, b) => b.price - a.price);
  if (!ds.length) return "";
  const dash = '<span class="dash">—</span>';
  const rows = ds.map((d) => `
      <tr>
        <td><span class="pname">${esc(d.name)}</span><span class="pcity">${esc(d.city)}${d.city && d.market ? " <i>·</i> " : ""}${esc(d.market)}</span></td>
        <td class="num">${d.sf ? d.sf.toLocaleString("en-US") : dash}</td>
        <td class="num strong">${d.price ? money(d.price) : dash}</td>
        <td class="num dim">${d.price && d.sf ? `$${Math.round(d.price / d.sf).toLocaleString("en-US")}` : dash}</td>
        <td><span class="chip ${d.on ? "on" : "off"}">${d.on ? "On-market" : "Off-market"}</span></td>
        <td class="broker">${d.broker ? esc(d.broker) : dash}</td>
        <td class="num">${d.cfo ? mdy(d.cfo) : dash}</td>
        <td class="num dim">${d.added ? mdy(d.added) : dash}</td>
      </tr>`).join("");
  const sf = ds.reduce((s, d) => s + d.sf, 0), px = ds.reduce((s, d) => s + d.price, 0);
  return `
  <section class="stage">
    <div class="stage-head">
      <h2>${esc(stage)}</h2>
      <span class="count">${ds.length} deal${ds.length === 1 ? "" : "s"}</span>
      <span class="rule"></span>
      <span class="sub">${sf.toLocaleString("en-US")} SF <i>·</i> ${money(px)}</span>
    </div>
    <div class="tablewrap"><table>
      <colgroup><col class="c-prop"><col class="c-sf"><col class="c-px"><col class="c-psf"><col class="c-src"><col class="c-brk"><col class="c-cfo"><col class="c-add"></colgroup>
      <thead><tr><th>Property</th><th class="num">SF</th><th class="num">Price</th><th class="num">$/SF</th><th>Sourcing</th><th>Broker / brokerage</th><th class="num">CFO</th><th class="num">Added</th></tr></thead>
      <tbody>${rows}
      </tbody>
    </table></div>
  </section>`;
}

/** File name the browser offers when saving the printout as PDF. */
export const reportTitle = (today) =>
  `ProximityParks - Pipeline Update - ${today.getMonth() + 1}.${today.getDate()}.${String(today.getFullYear()).slice(2)}`;

/** The standalone HTML document. logo / mark are absolute image URLs. */
export function renderReportHtml(r, { logo, mark }) {
  const { today, since, deals } = r;
  const span = `${monDay(since)} – ${monDay(today)}, ${today.getFullYear()}`;
  const totSf = deals.reduce((s, d) => s + d.sf, 0), totPx = deals.reduce((s, d) => s + d.price, 0);
  const nLoi = deals.filter((d) => d.stage === "LOI Submitted").length;
  const nOff = deals.filter((d) => !d.on).length;
  const nAdv = r.outcomes[0].n;
  const header = (title) => `
  <div class="split" aria-hidden="true"><span></span><span></span></div>
  <header>
    <img class="logo" src="${esc(logo)}" alt="ProximityParks">
    <div class="titles">
      <span class="eyebrow">Weekly pipeline update</span>
      <h1>${title}</h1>
    </div>`;
  const stack = r.outcomes.filter((o) => o.n).map((o) => `<span style="flex:${o.n};background:${o.color}" title="${esc(o.label)}: ${o.n}"></span>`).join("");
  const legend = r.outcomes.map((o) => `<li><span class="sw" style="background:${o.color}"></span><span class="lk">${esc(o.label)}</span><span class="lv">${o.n}</span></li>`).join("");

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8">
<title>${esc(reportTitle(today))}</title>
<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&display=swap">
<style>${CSS}</style>
</head><body>
<div class="stack-pages">
<main class="sheet activity">${header("Pipeline <b>Activity</b>")}
    <div class="asof"><strong>Rolling 30 days</strong>${span}</div>
  </header>

  <p class="lede">We review far more than we pursue. In the last 30 days <b>${r.entered} new deal${r.entered === 1 ? "" : "s"}</b> entered our pipeline and we
    <b>passed on ${r.passed}</b>. The ${deals.length} active deals on the next page are the ones that cleared our underwriting and strategy bar.</p>

  <div class="band four">
    <img class="mark" src="${esc(mark)}" alt="">
    <div class="stat"><span class="v">${r.entered}</span><span class="l">Deals entered pipeline</span><span class="s">${(r.enteredSf / 1e6).toFixed(2)}M SF reviewed</span></div>
    <div class="stat"><span class="v">${r.passed}</span><span class="l">Deals passed</span><span class="s">after review</span></div>
    <div class="stat hot"><span class="v">${r.lois}</span><span class="l">LOIs submitted</span><span class="s">in the same 30 days</span></div>
    <div class="stat"><span class="v">${deals.length}</span><span class="l">Active today</span><span class="s">see page 2</span></div>
  </div>

  <div class="panels">
    <section class="panel">
      <h3>Where the ${r.entered} new deals stand<span>today</span></h3>
      <div class="stackbar" role="img" aria-label="Outcome of deals that entered the pipeline">${stack}</div>
      <ul class="legend">${legend}</ul>
      <p class="callout"><b>${nAdv} of ${r.entered}</b>${r.entered ? ` (${Math.round(nAdv / r.entered * 100)}%)` : ""} advanced to underwriting or an LOI.</p>
    </section>
    <section class="panel reasons">
      <h3>Why we passed<span>${r.passed} deals</span></h3>
      ${bars(r.reasons)}
    </section>
    <section class="panel">
      <h3>New deals by market<span>${r.entered} deals</span></h3>
      ${bars(r.byMarket)}
    </section>
  </div>

  <footer>
    <p class="notes">Rolling window ${span}. Entered = deals added to our acquisitions pipeline in the window
      (broker-outreach LOIs excluded). Passed = deals we declined in the window, whenever they entered; not counted are ${r.lost} deals lost to
      competing bids and ${r.dead} that did not proceed. LOIs submitted counts distinct deals. Active = underwriting, LOI submitted or diligence.</p>
    <div class="foot"><span class="pg">— 01 —</span></div>
  </footer>
</main>

<main class="sheet">${header("Active <b>Pipeline</b>")}
    <div class="asof"><strong>${today.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</strong>Week ${isoWeek(today)} &nbsp;·&nbsp; ProximityParks USA</div>
  </header>

  <div class="band">
    <img class="mark" src="${esc(mark)}" alt="">
    <div class="stat"><span class="v">${deals.length}</span><span class="l">Active deals</span></div>
    <div class="stat hot"><span class="v">${nLoi}</span><span class="l">LOIs outstanding</span></div>
    <div class="stat"><span class="v">${(totSf / 1e6).toFixed(2)}<small>M SF</small></span><span class="l">Under review</span></div>
    <div class="stat"><span class="v">$${Math.round(totPx / 1e6).toLocaleString("en-US")}<small>M</small></span><span class="l">Aggregate price</span></div>
    <div class="stat"><span class="v">${nOff} <small>of ${deals.length}</small></span><span class="l">Sourced off-market</span></div>
  </div>
${stageTable(deals, "In Diligence")}${stageTable(deals, "LOI Submitted")}${stageTable(deals, "Underwriting")}
  <footer>
    <p class="notes">Price is ProximityParks' underwritten value as of this update; $/SF is price over building SF. On-market deals are in a broker-run
      marketing process; off-market deals came to us without one. Broker is the broker or brokerage that brought us the deal; CFO is the
      call-for-offers date; Added is the date the deal entered our pipeline. Closed acquisitions are excluded.</p>
    <div class="foot"><span class="pg">— 02 —</span></div>
  </footer>
</main>
</div>
</body></html>`;
}

// Tuned against Chromium's print engine for landscape Letter: the activity
// page is a fixed sheet, the deal tables flow onto further pages if needed.
const CSS = `
:root {
  --red: #e93529; --ink: #333333; --gray: #d8d8d8; --mist: #f4f4f4; --paper: #ffffff; --mute: #7d7a79;
  --font: "Montserrat", "Segoe UI", Arial, sans-serif;
  color-scheme: light;
}
* { box-sizing: border-box; }
html { background: var(--mist); }
body { margin: 0; background: var(--mist); color: var(--ink); font-family: var(--font); -webkit-print-color-adjust: exact; print-color-adjust: exact; padding: 24px 16px; }
.sheet { background: var(--paper); max-width: 1100px; margin: 0 auto; padding: 34px 44px 26px; display: flex; flex-direction: column; gap: 18px;
         box-shadow: 0 1px 2px rgba(51,51,51,.06), 0 8px 30px rgba(51,51,51,.08); }
.split { display: grid; grid-template-columns: 210px 1fr; gap: 14px; }
.split span { border-top: 1px solid var(--gray); }
header { display: grid; grid-template-columns: 210px 1fr auto; gap: 14px; align-items: end; }
header .logo { width: 190px; max-width: 100%; display: block; }
.titles { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.eyebrow { color: var(--red); font-size: 10.5px; font-weight: 600; letter-spacing: .14em; text-transform: uppercase; }
h1 { margin: 0; font-size: 30px; font-weight: 300; letter-spacing: .01em; line-height: 1.05; }
h1 b { font-weight: 700; }
.asof { text-align: right; font-size: 11px; color: var(--mute); line-height: 1.5; }
.asof strong { display: block; color: var(--ink); font-size: 13px; font-weight: 600; }
.band { position: relative; overflow: hidden; background: var(--ink); color: #fff; display: grid; grid-template-columns: repeat(5, 1fr); padding: 16px 22px; gap: 10px; }
.band .mark { position: absolute; right: -26px; top: -40px; height: 170px; opacity: .10; pointer-events: none; }
.stat { display: flex; flex-direction: column; gap: 3px; position: relative; padding-left: 14px; border-left: 1px solid rgba(255,255,255,.18); }
.stat:first-of-type { border-left: 0; padding-left: 0; }
.stat .v { font-size: 24px; font-weight: 700; letter-spacing: -.01em; font-variant-numeric: tabular-nums; }
.stat .v small { font-size: 13px; font-weight: 500; margin-left: 2px; }
.stat .l { font-size: 9.5px; letter-spacing: .13em; text-transform: uppercase; color: rgba(255,255,255,.72); font-weight: 500; }
.stat .s { font-size: 10px; color: rgba(255,255,255,.62); font-variant-numeric: tabular-nums; }
.stat.hot .v { color: #ff5a4e; }
.band.four { grid-template-columns: repeat(4, 1fr); }
.stage { display: flex; flex-direction: column; gap: 6px; }
.stage-head { display: flex; align-items: baseline; gap: 10px; }
.stage-head h2 { margin: 0; font-size: 12.5px; font-weight: 700; letter-spacing: .13em; text-transform: uppercase; color: var(--red); }
.stage-head .count { font-size: 11px; color: var(--mute); font-weight: 500; }
.stage-head .rule { flex: 1; border-top: 1px solid var(--gray); transform: translateY(-3px); }
.stage-head .sub { font-size: 11px; font-weight: 600; font-variant-numeric: tabular-nums; }
i { font-style: normal; color: var(--gray); margin: 0 3px; }
.tablewrap { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; font-size: 11px; min-width: 860px; table-layout: fixed; }
col.c-prop { width: 27%; } col.c-sf { width: 8.5%; } col.c-px { width: 8.5%; } col.c-psf { width: 6.5%; }
col.c-src { width: 11%; } col.c-brk { width: 19%; } col.c-cfo { width: 9%; } col.c-add { width: 10.5%; }
th { font-size: 8.5px; font-weight: 600; letter-spacing: .12em; text-transform: uppercase; color: var(--mute); text-align: left; padding: 5px 8px; background: var(--mist); }
td { padding: 5px 8px; border-bottom: 1px solid var(--mist); vertical-align: middle; }
tbody tr:last-child td { border-bottom: 1px solid var(--gray); }
tr { break-inside: avoid; }
.num { text-align: right; font-variant-numeric: tabular-nums; }
.strong { font-weight: 700; }
.dim { color: var(--mute); }
.pname { display: block; font-weight: 600; font-size: 11.5px; line-height: 1.3; }
.pcity { display: block; font-size: 9.5px; color: var(--mute); line-height: 1.3; }
.broker { font-weight: 500; }
.dash { color: var(--gray); }
.chip { display: inline-block; font-size: 8.5px; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; padding: 3px 7px 2px; border-radius: 999px; white-space: nowrap; }
.chip.off { background: var(--red); color: #fff; }
.chip.on { border: 1px solid var(--ink); color: var(--ink); padding: 2px 6px 1px; }
footer { display: flex; flex-direction: column; gap: 8px; margin-top: auto; }
.notes { margin: 0; font-size: 8.5px; color: var(--mute); line-height: 1.55; max-width: 120ch; }
.foot { display: flex; justify-content: flex-end; border-top: 1px solid var(--gray); padding-top: 7px; font-size: 8.5px; letter-spacing: .05em; color: var(--mute); }
.stack-pages { display: flex; flex-direction: column; gap: 24px; }
.lede { margin: 0; font-size: 13px; line-height: 1.6; max-width: 92ch; }
.lede b { font-weight: 700; }
.panels { display: grid; grid-template-columns: 1.15fr 1fr 1fr; gap: 28px; align-items: start; }
.panel { display: flex; flex-direction: column; gap: 12px; min-width: 0; }
.panel h3 { margin: 0; font-size: 11px; font-weight: 700; letter-spacing: .13em; text-transform: uppercase; color: var(--red); padding-bottom: 7px; border-bottom: 1px solid var(--gray); }
.panel h3 span { color: var(--mute); font-weight: 500; letter-spacing: .04em; text-transform: none; margin-left: 6px; }
.stackbar { display: flex; height: 26px; gap: 2px; }
.stackbar span { display: block; min-width: 4px; }
.legend { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 7px; font-size: 11px; }
.legend li { display: grid; grid-template-columns: 12px 1fr auto; gap: 9px; align-items: center; }
.sw { width: 12px; height: 12px; border: 1px solid rgba(51,51,51,.12); }
.lv { font-weight: 700; font-variant-numeric: tabular-nums; }
.callout { margin: 0; font-size: 11px; line-height: 1.5; color: var(--mute); }
.callout b { color: var(--ink); }
.bar { display: grid; grid-template-columns: minmax(0, 1.25fr) 1fr 22px; gap: 10px; align-items: center; font-size: 11px; }
.bl { min-width: 0; }
.bt { display: block; height: 10px; background: var(--mist); }
.bt span { display: block; height: 100%; background: var(--ink); }
.panel.reasons .bt span { background: var(--red); }
.bv { text-align: right; font-weight: 700; font-variant-numeric: tabular-nums; }
@page { size: 11in 8.5in; margin: 0; }
@media print {
  html, body { background: var(--paper); padding: 0; }
  .sheet { box-shadow: none; width: 11in; max-width: none; padding: .3in .5in .24in; gap: 12px; }
  .tablewrap { overflow: visible; }
  .sheet > * { flex-shrink: 0; }
  td { padding-block: 4.5px; }
  .pname, .pcity { line-height: 1.2; }
  .pname { font-size: 11px; }
  .pcity { font-size: 8.8px; }
  table { font-size: 10.5px; min-width: 0; }
  h1 { font-size: 27px; }
  .stage { gap: 4px; }
  th { padding-block: 4px; }
  .band { padding-block: 10px; }
  .stat .v { font-size: 22px; }
  footer { gap: 6px; }
  .stack-pages { display: block; }
  .sheet + .sheet { break-before: page; }
  .sheet.activity { height: 8.4in; gap: 22px; }
  .activity .lede { font-size: 14.5px; }
  .activity .band { padding-block: 20px; }
  .activity .stat .v { font-size: 34px; }
  .activity .stat .l { font-size: 10.5px; }
  .activity .stat .s { font-size: 11px; }
  .activity .panels { gap: 36px; margin-top: 6px; }
  .activity .panel { gap: 16px; }
  .activity .panel h3 { font-size: 12px; }
  .activity .stackbar { height: 40px; }
  .activity .legend { font-size: 12.5px; gap: 11px; }
  .activity .bar { font-size: 12.5px; }
  .activity .bt { height: 14px; }
  .activity .callout { font-size: 12.5px; }
  .activity .notes { font-size: 9px; }
}
`;
